import { PromoCode, PromoCodeUsage, PromoCodeUsageRecord, User } from '../types';
import { updateUser } from './firebaseService';
import customEffectsService from './customEffectsService';
import { BACKGROUND_PACKAGES } from '../config/chatBackgrounds';

export interface PromoCodeRedemptionResult {
    success: boolean;
    message?: string;
    updatedUser?: User;
    error?: string;
    appliedValue?: number; // The actual discount amount or tokens applied
}

export interface PromoValidationResult {
    valid: boolean;
    reason?: string;
    errorCode?: 'INACTIVE' | 'EXPIRED' | 'MAX_USES' | 'ALREADY_USED' | 'MIN_PURCHASE' | 'NOT_FOUND';
}

/**
 * Validate if a promo code can be used by a specific user
 */
export const validatePromoCode = (
    promo: PromoCode,
    userId?: string,
    purchaseAmount?: number
): PromoValidationResult => {
    // Check if active
    if (!promo.isActive) {
        return { valid: false, reason: 'This promo code is inactive', errorCode: 'INACTIVE' };
    }

    // Check expiry date
    if (promo.expiryDate) {
        const now = new Date();
        const expiry = new Date(promo.expiryDate);
        if (now > expiry) {
            return { valid: false, reason: 'This promo code has expired', errorCode: 'EXPIRED' };
        }
    }

    // Check max uses (global limit)
    if (promo.maxUses && promo.maxUses > 0 && promo.usedCount !== undefined) {
        if (promo.usedCount >= promo.maxUses) {
            return { valid: false, reason: 'This promo code has reached its usage limit', errorCode: 'MAX_USES' };
        }
    }

    // Check per-user usage (prevent reuse by same user)
    if (userId && promo.oneTimePerUser !== false) { // Default is one-time per user
        if (promo.usedByUsers?.includes(userId)) {
            return { valid: false, reason: 'You have already used this promo code', errorCode: 'ALREADY_USED' };
        }
    }

    // Check minimum purchase amount (for discount codes)
    if (promo.minPurchaseAmount && purchaseAmount !== undefined) {
        if (purchaseAmount < promo.minPurchaseAmount) {
            return {
                valid: false,
                reason: `Minimum purchase of $${promo.minPurchaseAmount.toFixed(2)} required`,
                errorCode: 'MIN_PURCHASE'
            };
        }
    }

    return { valid: true };
};

/**
 * Find a promo code by code string (case-insensitive)
 */
export const findPromoCode = (code: string, promoCodes: PromoCode[]): PromoCode | null => {
    const normalizedCode = code.trim().toUpperCase();
    return promoCodes.find(p => p.code.toUpperCase() === normalizedCode) || null;
};

/**
 * Calculate discount amount for a promo code
 */
export const calculateDiscount = (promo: PromoCode, originalPrice: number): number => {
    if (!promo.effect || promo.effect === 'discount') {
        if (promo.type === 'percent') {
            return originalPrice * ((promo.value || 0) / 100);
        } else {
            return Math.min(promo.value || 0, originalPrice);
        }
    }
    return 0;
};

/**
 * Check if promo code gives 100% discount (free)
 */
export const isFreeWithPromo = (promo: PromoCode, originalPrice: number): boolean => {
    if (!promo.effect || promo.effect === 'discount') {
        const discount = calculateDiscount(promo, originalPrice);
        return discount >= originalPrice;
    }
    // Premium trial, unlimited tokens, etc. are also "free" (no payment)
    return promo.effect === 'premium_trial' ||
        promo.effect === 'unlimited_tokens' ||
        promo.effect === 'token_boost' ||
        promo.effect === 'feature_unlock';
};

/**
 * Redeem a promo code for a user
 */
export const redeemPromoCode = async (
    promo: PromoCode,
    user: User,
    promoCodes: PromoCode[],
    savePromoCodes: (codes: PromoCode[]) => Promise<void>
): Promise<PromoCodeRedemptionResult> => {
    // Validate promo code
    const validation = validatePromoCode(promo, user.id);
    if (!validation.valid) {
        return {
            success: false,
            error: validation.reason || 'Invalid promo code'
        };
    }

    let updatedUser = { ...user };
    let message = '';
    let appliedValue = 0;

    try {
        // Apply effect based on type
        switch (promo.effect || 'discount') {
            case 'discount':
                // Discount is handled in payment flow, not here
                message = `💰 Discount code applied: ${promo.type === 'percent' ? `${promo.value || 0}% off` : `$${promo.value || 0} off`}`;
                appliedValue = promo.value || 0;
                break;

            case 'token_boost':
                // Initialize activePromoEffects if not exists
                if (!updatedUser.activePromoEffects) {
                    updatedUser.activePromoEffects = {};
                }

                // Apply one-time token boost - ADD to tokenBalance (NEW TOKEN SYSTEM)
                if (promo.tokenBoostAmount) {
                    // Get current token balance (default to tier limit if not set)
                    const tierTokenLimit = user.isMax ? 50000 : user.isPremium ? 15000 : 2000;
                    const currentBalance = user.tokenBalance ?? tierTokenLimit;

                    // ADD tokens to balance (can exceed tier limit)
                    updatedUser.tokenBalance = currentBalance + promo.tokenBoostAmount;
                    message = `⚡ You received ${promo.tokenBoostAmount.toLocaleString()} bonus tokens! New balance: ${updatedUser.tokenBalance.toLocaleString()}`;
                    appliedValue = promo.tokenBoostAmount;

                    console.log(`[PromoCode] Token boost applied: +${promo.tokenBoostAmount} tokens. Balance: ${currentBalance} -> ${updatedUser.tokenBalance}`);
                }

                // Token multiplier for ongoing effect
                if (promo.tokenBoostMultiplier && promo.tokenBoostMultiplier > 1) {
                    const multiplierDuration = promo.premiumDurationDays || 30;
                    const multiplierExpiry = new Date();
                    multiplierExpiry.setDate(multiplierExpiry.getDate() + multiplierDuration);

                    updatedUser.activePromoEffects.tokenMultiplier = promo.tokenBoostMultiplier;
                    updatedUser.activePromoEffects.tokenMultiplierUntil = multiplierExpiry.toISOString();
                    message += message ? ` Token generation multiplied by ${promo.tokenBoostMultiplier}x for ${multiplierDuration} days!` : `⚡ Token generation multiplied by ${promo.tokenBoostMultiplier}x for ${multiplierDuration} days!`;
                }
                break;

            case 'premium_trial':
                // Grant premium trial
                const trialDuration = promo.premiumDurationDays || 30;
                const now = new Date();
                let newExpiry = new Date();
                let newStart = now.toISOString();

                if (user.isPremium && user.premiumExpiryDate) {
                    // Extend existing subscription
                    const currentExpiry = new Date(user.premiumExpiryDate);
                    const baseDate = currentExpiry > now ? currentExpiry : now;
                    baseDate.setDate(baseDate.getDate() + trialDuration);
                    newExpiry = baseDate;
                    newStart = user.premiumStartDate || now.toISOString();
                    message = `👑 Premium subscription extended by ${trialDuration} days!`;
                } else {
                    // New subscription
                    newExpiry.setDate(newExpiry.getDate() + trialDuration);
                    message = `👑 Premium trial activated for ${trialDuration} days!`;
                }

                updatedUser.isPremium = true;
                updatedUser.premiumStartDate = newStart;
                updatedUser.premiumExpiryDate = newExpiry.toISOString();
                appliedValue = trialDuration;
                break;

            case 'unlimited_tokens':
                // Grant unlimited tokens for duration
                // Check for minutes (gift codes) or days (regular promos)
                const unlimitedMinutes = (promo as any).unlimitedMinutes;
                const unlimitedExpiry = new Date();
                let durationMessage = '';

                if (unlimitedMinutes && unlimitedMinutes > 0) {
                    // Use minutes (from gift codes)
                    unlimitedExpiry.setTime(unlimitedExpiry.getTime() + unlimitedMinutes * 60 * 1000);
                    if (unlimitedMinutes >= 1440) {
                        durationMessage = `${Math.floor(unlimitedMinutes / 1440)} day(s)`;
                    } else if (unlimitedMinutes >= 60) {
                        durationMessage = `${Math.floor(unlimitedMinutes / 60)} hour(s)`;
                    } else {
                        durationMessage = `${unlimitedMinutes} minute(s)`;
                    }
                    appliedValue = unlimitedMinutes;
                } else {
                    // Use days (from regular promos)
                    const unlimitedDays = promo.premiumDurationDays || 30;
                    unlimitedExpiry.setDate(unlimitedExpiry.getDate() + unlimitedDays);
                    durationMessage = `${unlimitedDays} day(s)`;
                    appliedValue = unlimitedDays;
                }

                // Initialize activePromoEffects if not exists
                if (!updatedUser.activePromoEffects) {
                    updatedUser.activePromoEffects = {};
                }

                updatedUser.activePromoEffects.unlimitedTokensUntil = unlimitedExpiry.toISOString();
                updatedUser.dailyTokenUsage = 0; // Reset current usage
                message = `🚀 Unlimited tokens activated for ${durationMessage}!`;
                break;

            case 'feature_unlock':
                // Check if this is a custom effect
                if (promo.customEffectId) {
                    try {
                        const result = await customEffectsService.activateEffect(
                            promo.customEffectId,
                            user,
                            'promo',
                            promo.code
                        );

                        if (!result.success) {
                            return {
                                success: false,
                                error: result.message || 'Failed to activate custom effect'
                            };
                        }

                        message = result.message;
                        appliedValue = 1;
                    } catch (error) {
                        console.error('[PromoCode] Custom effect error:', error);
                        return {
                            success: false,
                            error: 'Unable to execute custom effect'
                        };
                    }
                } else {
                    // Standard feature unlock
                    // Initialize activePromoEffects if not exists
                    if (!updatedUser.activePromoEffects) {
                        updatedUser.activePromoEffects = {};
                    }

                    const featureDuration = promo.premiumDurationDays || 30;
                    const featureExpiry = new Date();
                    featureExpiry.setDate(featureExpiry.getDate() + featureDuration);

                    updatedUser.activePromoEffects.unlockedFeatures = promo.featureUnlocks || [];
                    updatedUser.activePromoEffects.featureUnlocksUntil = featureExpiry.toISOString();

                    const featureNames = promo.featureUnlocks?.join(', ') || 'special features';
                    message = `🔓 ${featureNames} unlocked for ${featureDuration} days!`;
                    appliedValue = featureDuration;
                }
                break;

            case 'background_unlock':
                // Unlock a chat background
                if (promo.chatBackgroundId) {
                    const currentUnlocked = updatedUser.unlockedBackgrounds || [];

                    if (promo.chatBackgroundId === 'ALL_BACKGROUNDS') {
                        // Unlock ALL backgrounds defined in config
                        const allIds = BACKGROUND_PACKAGES.map(bg => bg.id);
                        const newUnlocked = Array.from(new Set([...currentUnlocked, ...allIds]));
                        updatedUser.unlockedBackgrounds = newUnlocked;
                        message = `🎨 All premium chat backgrounds unlocked!`;
                        appliedValue = allIds.length;
                    } else if (!currentUnlocked.includes(promo.chatBackgroundId)) {
                        updatedUser.unlockedBackgrounds = [...currentUnlocked, promo.chatBackgroundId];
                        message = `🎨 New chat background unlocked: ${promo.chatBackgroundId}!`;
                        appliedValue = 1;
                    } else {
                        message = `🎨 You already have this background unlocked.`;
                        appliedValue = 0;
                    }
                } else {
                    return {
                        success: false,
                        error: 'No background specified in promo code'
                    };
                }
                break;

            default:
                return {
                    success: false,
                    error: 'Unknown promo code effect'
                };
        }

        // Update user in Firebase
        await updateUser(user.id, updatedUser);

        // Create usage history record
        const usageRecord: PromoCodeUsageRecord = {
            userId: user.id,
            username: user.username || 'Unknown',
            usedAt: new Date().toISOString(),
            effect: promo.effect || 'discount',
            valueApplied: appliedValue
        };

        // Increment usage count and track user
        const updatedPromoCodes = promoCodes.map(p => {
            if (p.code === promo.code) {
                return {
                    ...p,
                    usedCount: (p.usedCount || 0) + 1,
                    usedByUsers: [...(p.usedByUsers || []), user.id],
                    usageHistory: [...(p.usageHistory || []), usageRecord]
                };
            }
            return p;
        });
        await savePromoCodes(updatedPromoCodes);

        return {
            success: true,
            message,
            updatedUser,
            appliedValue
        };
    } catch (error) {
        console.error('Error redeeming promo code:', error);
        return {
            success: false,
            error: 'Failed to redeem promo code. Please try again.'
        };
    }
};

/**
 * Get promo code statistics
 */
export const getPromoCodeStats = (promoCodes: PromoCode[]) => {
    const total = promoCodes.length;
    const active = promoCodes.filter(p => p.isActive).length;
    const expired = promoCodes.filter(p => {
        if (!p.expiryDate) return false;
        return new Date(p.expiryDate) < new Date();
    }).length;
    const exhausted = promoCodes.filter(p => {
        if (!p.maxUses || p.maxUses === 0) return false;
        return (p.usedCount || 0) >= p.maxUses;
    }).length;
    const totalRedemptions = promoCodes.reduce((sum, p) => sum + (p.usedCount || 0), 0);

    return {
        total,
        active,
        inactive: total - active,
        expired,
        exhausted,
        totalRedemptions,
        byCategory: {
            holiday: promoCodes.filter(p => p.category === 'holiday').length,
            partnership: promoCodes.filter(p => p.category === 'partnership').length,
            affiliate: promoCodes.filter(p => p.category === 'affiliate').length,
            loyalty: promoCodes.filter(p => p.category === 'loyalty').length,
            special: promoCodes.filter(p => p.category === 'special').length,
            general: promoCodes.filter(p => !p.category || p.category === 'general').length,
        },
        byEffect: {
            discount: promoCodes.filter(p => !p.effect || p.effect === 'discount').length,
            token_boost: promoCodes.filter(p => p.effect === 'token_boost').length,
            premium_trial: promoCodes.filter(p => p.effect === 'premium_trial').length,
            unlimited_tokens: promoCodes.filter(p => p.effect === 'unlimited_tokens').length,
            feature_unlock: promoCodes.filter(p => p.effect === 'feature_unlock').length,
        }
    };
};

/**
 * Generate a random promo code
 */
export const generatePromoCode = (prefix: string = '', length: number = 8): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = prefix ? prefix.toUpperCase() + '-' : '';
    for (let i = 0; i < length; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
};

/**
 * Generate multiple promo codes with the same settings
 */
export const generateBulkPromoCodes = (
    baseSettings: Partial<PromoCode>,
    count: number,
    prefix: string = ''
): PromoCode[] => {
    const codes: PromoCode[] = [];
    const existingCodes = new Set<string>();

    for (let i = 0; i < count; i++) {
        let code: string;
        do {
            code = generatePromoCode(prefix);
        } while (existingCodes.has(code));

        existingCodes.add(code);
        codes.push({
            ...baseSettings,
            code,
            isActive: true,
            usedCount: 0,
            usedByUsers: [],
            usageHistory: [],
            createdAt: new Date().toISOString(),
            type: baseSettings.type || 'percent',
            value: baseSettings.value || 0
        } as PromoCode);
    }

    return codes;
};
